// The 16 principal Odù Ifá, in canonical order. `code` is the tonal Yorùbá
// spelling shown in the UI; `name` is the plain ASCII form used to compose and
// persist `collectionName` (keeping it consistent with the rest of the admin app
// and with search/list rendering). Ported from the legacy `baseDashboardData`.
export interface Odu {
  code: string; // tonal display, e.g. "OGBÈ"
  name: string; // ASCII, e.g. "OGBE"
}

export const ODU: Odu[] = [
  { code: 'OGBÈ',       name: 'OGBE' },
  { code: 'Ọ̀YẸ̀KÚ',      name: 'OYEKU' },
  { code: 'ÌWÒRÌ',      name: 'IWORI' },
  { code: 'ÒDÍ',        name: 'ODI' },
  { code: 'ÌROSÙN',     name: 'IROSUN' },
  { code: 'Ọ̀WỌ́NRÍN',    name: 'OWONRIN' },
  { code: 'Ọ̀BÀRÀ',      name: 'OBARA' },
  { code: 'Ọ̀KÀNRÀN',    name: 'OKANRAN' },
  { code: 'ÒGÚNDÁ',     name: 'OGUNDA' },
  { code: 'Ọ̀SÁ',        name: 'OSA' },
  { code: 'ÌKÁ',        name: 'IKA' },
  { code: 'ÒTÚRÚPỌ̀N',   name: 'OTURUPON' },
  { code: 'ÒTÚRÁ',      name: 'OTURA' },
  { code: 'ÌRẸTẸ̀',      name: 'IRETE' },
  { code: 'Ọ̀SẸ́',        name: 'OSE' },
  { code: 'ÒFÚN',       name: 'OFUN' },
];

const BY_NAME = new Map(ODU.map(o => [o.name, o]));

/**
 * Compose the persisted `collectionName` from a major + minor Odù selection.
 * Rules ported verbatim from the legacy MediaForm:
 *   - same Odù, OGBE   → "EJI OGBE"
 *   - same Odù, other  → "<NAME> MEJI"
 *   - different Odù    → "<MAJOR> <MINOR>"
 */
export function composeCollectionName(major: Odu, minor: Odu): string {
  if (major.name === minor.name) {
    return major.name === 'OGBE' ? 'EJI OGBE' : `${major.name} MEJI`;
  }
  return `${major.name} ${minor.name}`;
}

/**
 * Reverse-map an existing `collectionName` back to its major/minor Odù so the
 * picker can pre-select when editing. All ASCII Odù names are single tokens, so
 * splitting on whitespace is unambiguous.
 */
export function parseCollectionName(collectionName?: string): { major?: Odu; minor?: Odu } {
  if (!collectionName) return {};
  const value = collectionName.trim().toUpperCase();

  if (value === 'EJI OGBE') {
    const ogbe = BY_NAME.get('OGBE');
    return { major: ogbe, minor: ogbe };
  }

  const parts = value.split(/\s+/);
  if (parts.length === 2 && parts[1] === 'MEJI') {
    const odu = BY_NAME.get(parts[0]);
    return { major: odu, minor: odu };
  }
  if (parts.length === 2) {
    return { major: BY_NAME.get(parts[0]), minor: BY_NAME.get(parts[1]) };
  }
  return {};
}
